import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale } from '@/i18n/locales';
import { i18n } from '@/i18n';
import HomePage from '@/pages/HomePage.vue';
//import { useCalculatorStore } from '@/stores/calculator';
import { useBriefStore } from '@/stores/brief';
import { isSubproductId } from '@/content/subproducts';

const langPattern = `:lang(${SUPPORTED_LOCALES.join('|')})`;

/**
 * Определяет язык для корневого редиректа.
 *
 *   1. `localStorage['bafsy.locale']`, если валиден;
 *   2. `navigator.language.startsWith('en')` → `en`;
 *   3. `navigator.language.startsWith('ru')` → `ru`;
 *   4. Иначе `DEFAULT_LOCALE`.
 *
 * В SSR-контексте (нет window) — возвращает `DEFAULT_LOCALE`.
 */
function detectLangForRedirect(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const stored = window.localStorage.getItem('bafsy.locale');
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
  } catch {
    // localStorage заблокирован — игнорируем.
  }
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith('en')) return 'en';
    if (lang.startsWith('ru')) return 'ru';
  }
  return DEFAULT_LOCALE;
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => ({ path: `/${detectLangForRedirect()}/` }),
  },
  {
    path: `/${langPattern}`,
    component: () => import('@/components/layouts/AppLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: HomePage,
      },
      {
        path: 'brief',
        name: 'brief',
        component: () => import('@/pages/BriefPage.vue'),
        beforeEnter: () => {
          try {
            const store = useBriefStore();
            if (store.hasDraft) store.resumeDraft();
          } catch {
            // Pinia ещё не подключён в SSR / тесте — пропускаем.
          }
          return true;
        },
      },
      /*{
        path: 'calculator',
        name: 'calculator',
        component: () => import('@/pages/CalculatorPage.vue'),
        beforeEnter: () => {
          try {
            const store = useCalculatorStore();
            if (store.hasDraft) store.resumeDraft();
          } catch {
            // см. выше
          }
          return true;
        },
      },*/
      {
        path: 'solutions/:slug',
        name: 'subproduct',
        component: () => import('@/pages/SubproductPage.vue'),
        beforeEnter: (to) => {
          const slug = String(to.params.slug ?? '');
          if (!isSubproductId(slug)) {
            return { name: 'not-found-lang-catchall', params: to.params };
          }
          return true;
        },
      },
      {
        path: 'privacy',
        name: 'privacy',
        component: () => import('@/pages/PrivacyPage.vue'),
      },
      {
        path: '404',
        name: 'not-found',
        component: () => import('@/pages/NotFoundPage.vue'),
      },
      {
        path: ':pathMatch(.*)*',
        name: 'not-found-lang-catchall',
        component: () => import('@/pages/NotFoundPage.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found-global-catchall',
    redirect: (to) => `/${detectLangForRedirect()}/404?from=${encodeURIComponent(to.fullPath)}`,
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (to.hash) {
      return {
        el: to.hash,
        behavior: reduced ? 'auto' : 'smooth',
        top: 80,
      };
    }
    return { top: 0, behavior: reduced ? 'auto' : 'smooth' };
  },
});

function syncLocaleFromRoute(to: RouteLocationNormalized): void {
  const lang = to.params.lang as string | undefined;
  if (!lang) return;
  if (!(SUPPORTED_LOCALES as readonly string[]).includes(lang)) return;
  if (i18n.global.locale.value !== lang) {
    i18n.global.locale.value = lang as Locale;
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
}

router.beforeEach((to) => {
  syncLocaleFromRoute(to);
});

/**
 * Просмотр страницы в Метрику при переходе внутри сайта.
 *
 * Счётчик инициализируется один раз в index.html, а маршруты меняются без
 * перезагрузки — без этого хука Метрика видела только первую страницу визита.
 * Отсюда и брались «глубина 1.0» и «14 секунд на сайте» во всех отчётах.
 * Первую навигацию пропускаем: её уже засчитал init счётчика.
 */
let firstNavigationDone = false;
router.afterEach((to, from) => {
  if (typeof window === 'undefined') return;
  if (!firstNavigationDone) {
    firstNavigationDone = true;
    return;
  }
  const ym = window.ym;
  if (!ym) return;
  const id = import.meta.env.VITE_YM_ID;
  if (!id) return;
  // короткая пауза: к этому моменту страница успевает проставить свой <title>.
  // Именно setTimeout, а не requestAnimationFrame — тот не срабатывает,
  // пока вкладка в фоне, и просмотры терялись бы у половины посетителей.
  setTimeout(() => {
    ym(id, 'hit', window.location.origin + to.fullPath, {
      referer: window.location.origin + from.fullPath,
      title: document.title,
    });
  });
});
